import {watch} from 'vue'
import {useEventBus, useEventListener} from '@vueuse/core'
import {useAppStore} from '@/stores'
import {useStreamStore} from '@/stores/streamStore'

type Kind = 'video' | 'audio'

// A track end and the permission change behind it arrive together
const LOST_DELAY = 1000
// useUserMedia stops the old stream before it asks for the new one (device switch, the snapshot's resolution change and
// its restore, Restart): a capture still missing after this has failed
const MISSING_DELAY = 5000

// Gets a lost camera or microphone back during the call (black screen fixes of review M47, M48 and M13). On master this
// is PermissionsProcedure; on dev it went with usePermissions.ts in 8df6a29, and since then nothing listened to
// streamStore's 'stream:track:end' or asked again for a kind whose capture had failed:
// - a track that ended by itself (unplugged, OS privacy switch, taken by another app): streamStore emits stream:track:end
// - a capture that failed: the kind is on but has no stream (useUserMedia leaves none when getUserMedia fails, and its
//   constraint watch restarts only a kind that has a stream, so the restore after a failed snapshot capture never ran)
// - back on the page: a kind lost while the page was hidden, or whose recovery failed meanwhile. Before the batch, a
//   client whose camera was missing was taken out of the session on its next return to the page (M13)
// streamStore.recover captures the selected device again and retries a device that is still busy (1 s, 2 s).
// Only for a kind that is on (camEnabled/micEnabled) and that the browser allows without asking: a 'prompt' would ask
// on every return (Chrome blocks the site after 3 dismissals) and 'denied' cannot succeed (streamStore's permission
// watch asks again once it is allowed). Not queryable (Firefox < 131): tried, as on master
export const useMediaRecoveryCallback = () => {
  const app = useAppStore()
  const streams = useStreamStore()
  const running: Partial<Record<Kind, Promise<void> | null>> = {}
  const timers: Partial<Record<Kind, ReturnType<typeof setTimeout>>> = {}
  const due: Partial<Record<Kind, number>> = {}

  const streamOf = (kind: Kind) => (kind === 'video' ? streams.camStream : streams.micStream)
  const wanted = (kind: Kind) => {
    const enabled = kind === 'video' ? streams.camEnabled : streams.micEnabled
    const access = kind === 'video' ? streams.cameraAccess : streams.microphoneAccess
    // A snapshot is switching the camera to its full resolution and back (useLocalSnapshot): that restart is its own
    const snapshotting = kind === 'video' && !!streams.camResolution
    return app.isCall && enabled && !snapshotting && (access === 'granted' || access === undefined)
  }
  const missing = (kind: Kind) => !streamOf(kind)?.getTracks().some(track => track.readyState === 'live')

  // Single flight per kind: the three paths can overlap and must not capture the same kind twice
  const recover = (kind: Kind) => {
    if (running[kind] || !wanted(kind) || !missing(kind)) return
    console.info(`[mediaRecovery] The ${kind} is lost, capturing it again`)
    running[kind] = streams
      .recover(kind)
      .catch(error => console.error(`[mediaRecovery] Getting the ${kind} back failed:`, error))
      .finally(() => {
        running[kind] = null
      })
  }
  // A track end reaches both the event and the watch below: the earlier of the scheduled times wins
  const recoverLater = (kind: Kind, delay: number) => {
    const at = Date.now() + delay
    if (timers[kind] !== undefined && (due[kind] ?? Infinity) <= at) return
    clearTimeout(timers[kind])
    due[kind] = at
    timers[kind] = setTimeout(() => {
      timers[kind] = undefined
      due[kind] = undefined
      recover(kind)
    }, delay)
  }

  useEventBus('stream:track:end').on((event: any) => {
    const kind = event?.name === 'video_capture' ? 'video' : event?.name === 'audio_capture' ? 'audio' : undefined
    if (kind) recoverLater(kind, LOST_DELAY)
  })

  // Also when another device gets selected meanwhile (the lost one was unplugged): the constraint watch of useUserMedia
  // does not restart a kind without a stream
  for (const kind of ['video', 'audio'] as Kind[]) {
    watch(
      () => [
        !streamOf(kind),
        kind === 'video' ? streams.camLost : streams.micLost,
        wanted(kind),
        kind === 'video' ? streams.selectedCameraId : streams.selectedMicId,
      ],
      ([noStream, lost, isWanted]) => {
        if ((noStream || lost) && isWanted) recoverLater(kind, lost ? LOST_DELAY : MISSING_DELAY)
      }
    )
  }

  useEventListener(document, 'visibilitychange', () => {
    if (document.visibilityState !== 'visible') return
    for (const kind of ['video', 'audio'] as Kind[]) recover(kind)
  })
}
